/**
 * Buscar jugadores mientras se escribe.
 *
 * Tres cosas que un buscador en vivo tiene que resolver, y que son la razón de
 * que esto sea un hook y no cuatro líneas dentro de una pantalla:
 *
 *  1. NO BUSCAR EN CADA TECLA. Se espera a que la persona deje de escribir un
 *     momento. Sin eso, «rodriguez» son nueve consultas de las que ocho no le
 *     interesan a nadie.
 *
 *  2. CANCELAR LA ANTERIOR. Si no, la respuesta de «jos» puede llegar DESPUÉS
 *     de la de «jose» y pisarla: la lista muestra resultados de algo que ya no
 *     está escrito. Es el fallo clásico de los buscadores en vivo y no se ve
 *     casi nunca en desarrollo, donde la red va instantánea.
 *
 *  3. NO PARPADEAR. Mientras llega lo nuevo se siguen viendo los resultados
 *     viejos, en vez de vaciar la lista y volver a llenarla.
 */
import { useEffect, useRef, useState } from 'react';
import { SEXES } from '@tennisup/contracts';
import type { CategoryLevel, PlayerSearchResult, Sex } from '@tennisup/contracts';

import { AbortedError, api } from '../lib/api';

/**
 * La clave «NIVEL/SEXO» de vuelta a categoría.
 *
 * La categoría viaja como cadena y no como objeto porque el efecto la compara
 * por identidad: un objeto nuevo en cada dibujo dispararía una búsqueda nueva
 * en cada dibujo. Acá se desarma una sola vez, justo antes de pedir.
 */
/**
 * LA CLAVE DEL FILTRO DE BÚSQUEDA: nivel y sexo DE PERSONA.
 *
 * NO ES `categoryKey`, y por eso tiene su propia función. Aquella identifica un
 * CUADRO y lleva tres partes con la disciplina; esta dice «quién puede jugar
 * este nivel», que es una pregunta sobre personas: el mixto no cabe acá porque
 * nadie es de sexo mixto.
 *
 * Se escribe donde se lee —al lado de `leerClave`— para que las dos mitades no
 * se separen: escrita a mano en la pantalla, el día que el formato cambie una
 * de las dos se queda vieja y la búsqueda devuelve todo o nada sin avisar.
 */
export function claveDeBusqueda(level: CategoryLevel, sex: Sex): string {
  return `${level}/${sex}`;
}

function leerClave(clave: string | null): { level: CategoryLevel; sex: Sex } | null {
  if (clave === null) return null;
  const [level, sex] = clave.split('/');
  if (level === undefined || sex === undefined) return null;
  /*
   * `MIXED` NO ES EL SEXO DE NADIE, así que como filtro no devuelve a nadie.
   *
   * Esta función recibe la llave de una CATEGORÍA y el servidor busca PERSONAS:
   * son dos tipos distintos y el `as Sex` que había acá los igualaba a la
   * fuerza. Con un cuadro mixto el filtro salía «sex=MIXED» y la búsqueda
   * contestaba cero —sin error, sin aviso, una lista vacía que parece que no
   * hay nadie en el club—.
   *
   * Sin filtro es mejor que con un filtro que no puede acertar: quien busca
   * escribe el nombre y ve resultados. Para buscar a la pareja correcta está
   * `partnerSexFor`, que sí sabe a quién le toca.
   */
  if (!SEXES.includes(sex as Sex)) return null;
  return { level: level as CategoryLevel, sex: sex as Sex };
}

/** Desde cuántas letras se busca. Menos que esto devuelve media base de datos. */
export const MIN_SEARCH_LENGTH = 3;

/** Cuánto se espera después de la última tecla. Lo que tarda una pausa al escribir. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface PlayerSearchState {
  results: PlayerSearchResult[];
  searching: boolean;
  /**
   * El texto es demasiado corto todavía Y no hay categoría puesta.
   *
   * Mira las dos cosas porque son dos formas de acotar y basta una: con una
   * categoría elegida, tres letras dejaron de ser obligatorias y decir «seguí
   * escribiendo» sería pedir algo que ya no hace falta.
   */
  tooShort: boolean;
  /** Había más de los que caben. La pantalla lo dice y pide afinar. */
  hasMore: boolean;
  error: unknown;
}

export function usePlayerSearch(
  communityId: string,
  query: string,
  /**
   * Quién puede jugar esta categoría. `null` = no filtrar.
   *
   * SE PASA COMO CLAVE, no como objeto: un objeto nuevo en cada dibujo cambia
   * de identidad aunque diga lo mismo, y el efecto volvería a buscar sin
   * parar. La clave es una cadena, así que compara por valor.
   */
  canPlayKey: string | null = null,
): PlayerSearchState {
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // El controlador de la petición en vuelo, para poder cancelarla. En una ref
  // y no en estado: cambiarlo no tiene que repintar nada.
  const enVuelo = useRef<AbortController | null>(null);

  const limpio = query.trim();
  const hayTexto = limpio.length >= MIN_SEARCH_LENGTH;
  const tooShort = canPlayKey === null && limpio.length > 0 && !hayTexto;

  useEffect(() => {
    enVuelo.current?.abort();

    /*
     * HACE FALTA AL MENOS UNA FORMA DE ACOTAR: texto o categoría.
     *
     * Antes era solo el texto, y por eso no se podía mirar una categoría sin
     * escribir un nombre. El corte no desaparece —sin nada que acotar, la
     * primera tecla se bajaría la tabla entera— pero ahora la categoría
     * también acota.
     *
     * Con texto A MEDIO ESCRIBIR y una categoría puesta se busca SOLO por la
     * categoría: mandar una o dos letras daría resultados casi al azar que se
     * corrigen solos al tercer carácter, y ese parpadeo se ve.
     */
    if (!hayTexto && canPlayKey === null) {
      setResults([]);
      setHasMore(false);
      setSearching(false);
      setError(null);
      return;
    }

    setSearching(true);
    setError(null);

    const timer = setTimeout(() => {
      const controller = new AbortController();
      enVuelo.current = controller;

      const canPlay = leerClave(canPlayKey);

      api
        .searchPlayers(communityId, hayTexto ? limpio : '', canPlay, controller.signal)
        .then((pagina) => {
          /*
           * `Array.isArray` Y NO `?? []`.
           *
           * El tipo es una PROMESA SOBRE EL CONTRATO, no una garantía sobre el
           * cable: un servidor viejo que todavía devuelve el arreglo pelado
           * hace que `pagina.players` sea `undefined`, y `undefined.length`
           * revienta la pantalla entera en blanco. `?? []` tampoco alcanza,
           * porque no atrapa un objeto ni una cadena.
           */
          setResults(Array.isArray(pagina?.players) ? pagina.players : []);
          setHasMore(pagina?.hasMore === true);
          setSearching(false);
        })
        .catch((caught: unknown) => {
          // Cancelada porque la persona siguió escribiendo: no es un error y
          // no tiene que apagar el indicador, porque ya hay otra en camino.
          if (caught instanceof AbortedError) return;
          setError(caught);
          setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      // Al desmontar, o al cambiar lo escrito, se corta lo que esté en vuelo.
      enVuelo.current?.abort();
    };
  }, [communityId, limpio, hayTexto, canPlayKey]);

  return { results, searching, tooShort, hasMore, error };
}
